import React, { Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../services/api/apiService';
import { getLeagueFormat } from '../leagueFormat';
import { openLeagueEntry } from '../leagueStats';
import { ChartCard, ChartsFallback, PointsByTeamChart, WinRaceChart, WinsByTeamChart } from '../charts';

/**
 * LeagueStandingsTab Component
 * The standings table (teams, players or pairs) and, under "Charts", wins by
 * team, the season win race and points for / against. A row opens the
 * entry's page in the league.
 */
// Sports whose games can end level: they get a draws column
const DRAW_SPORTS = ['football', 'soccer'];

const StandingsCharts = ({ league }) => {
  const [state, setState] = useState({ loading: true, history: null, error: null });

  useEffect(() => {
    let alive = true;
    apiClient.get(`/leagues/${league.id}/standings/history`)
      .then((res) => alive && setState({ loading: false, history: res.data?.data || null, error: null }))
      .catch((err) => alive && setState({ loading: false, history: null, error: err.response?.data?.message || 'Could not load the charts.' }));
    return () => { alive = false; };
  }, [league.id]);

  if (state.loading) return <p className="league-team-page__note">Loading charts…</p>;
  if (state.error) return <p className="league-team-page__note" role="alert">{state.error}</p>;
  if (!state.history?.nights?.length) return <p className="league-team-page__empty">No results yet</p>;
  return (
    <Suspense fallback={<ChartsFallback />}>
      <div className="league-charts-grid">
        <ChartCard title="Wins by team">
          <WinsByTeamChart history={state.history} />
        </ChartCard>
        <ChartCard title="Season win race">
          <WinRaceChart history={state.history} />
        </ChartCard>
        <ChartCard title="Points for and against">
          <PointsByTeamChart history={state.history} />
        </ChartCard>
      </div>
    </Suspense>
  );
};

const LeagueStandingsTab = ({ standings, loading, league, rules = null }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('table');
  const { unit, unitLower } = getLeagueFormat(league);
  // Columns follow the league's rules (PR 8): ranked by wins puts W first and
  // hides Pts; draws and sets columns show only where they can happen
  const showDraws = rules ? Boolean(rules.draws_allowed) : DRAW_SPORTS.includes(String(league?.sport_type || '').toLowerCase());
  const byWins = rules?.rank_by === 'wins';
  const showSets = Boolean(rules?.uses_sets) && standings.some((t) => (t.sets_won || 0) + (t.sets_lost || 0) > 0);
  const open = (row) => openLeagueEntry(navigate, league, row, 'standings');
  const columns = [
    ...(byWins ? [['W', (t) => t.wins || 0, 'wins-cell']] : []),
    ['GP', (t) => t.games_played || 0],
    ...(byWins ? [] : [['W', (t) => t.wins || 0, 'wins-cell']]),
    ...(showDraws ? [['D', (t) => t.draws || 0]] : []),
    ['L', (t) => t.losses || 0, 'losses-cell'],
    // the API serves win % as 0-100 (LG-B28)
    ['Win%', (t) => `${Number(t.win_percentage || 0).toFixed(1)}%`],
    ...(showSets ? [['SW', (t) => t.sets_won || 0], ['SL', (t) => t.sets_lost || 0]] : []),
    ['PF', (t) => t.points_for || 0],
    ['PA', (t) => t.points_against || 0],
    ...(byWins ? [] : [['Pts', (t) => t.league_points || 0]]),
  ];

  return (
    <div className="standings-content">
      <div className="content-header">
        <h3>League Standings</h3>
        {standings.length > 0 && (
          <div className="league-view-toggle" role="group" aria-label="Standings view">
            {['table', 'charts'].map((v) => (
              <button
                key={v}
                type="button"
                className={`league-view-toggle__btn${view === v ? ' is-active' : ''}`}
                aria-pressed={view === v}
                onClick={() => setView(v)}
              >
                {v === 'table' ? 'Table' : 'Charts'}
              </button>
            ))}
          </div>
        )}
      </div>

      {standings.length === 0 && (
        <div className="empty-content">
          <p>No standings data available yet.</p>
          <p>Start playing games to see {unitLower} standings!</p>
        </div>
      )}

      {standings.length > 0 && view === 'charts' && <StandingsCharts league={league} />}

      {standings.length > 0 && view === 'table' && (
        <div className="standings-table">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>{unit}</th>
                {columns.map(([label]) => <th key={label}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {standings.map((team, index) => (
                <tr
                  key={team.league_team_id || team.team_id || index}
                  className="standings-row--link"
                  onClick={() => open(team)}
                >
                  <td className="rank-cell">{team.position || team.ranking || index + 1}</td>
                  <td className="team-name-cell">
                    <button
                      type="button"
                      className="standings-team-link"
                      onClick={(e) => { e.stopPropagation(); open(team); }}
                    >
                      {team.team_name}
                    </button>
                    {team.withdrawn && <span className="standings-withdrawn">Withdrawn</span>}
                  </td>
                  {columns.map(([label, value, cls]) => <td key={label} className={cls}>{value(team)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeagueStandingsTab;
